import { useEffect, useRef, useState } from 'react'
import useCRMStore from '../store/useCRMStore'

// Duración del fade verde para nuevos prospectos. Exportado para que componentes
// de UI (animaciones CSS, badges) puedan referenciar el mismo valor.
export const NEW_ROW_HIGHLIGHT_MS = 5000

/**
 * Hook que maneja el lifecycle del frozen mode de la grilla TodoList:
 * - Tick cada 1s para actualizar el countdown UI (mientras frozen.active).
 * - setTimeout que dispara exitFrozen cuando se alcanza el deadline.
 * - Limpieza per-phone de newRowsHighlight tras NEW_ROW_HIGHLIGHT_MS, con
 *   reschedule cuando el mismo phone vuelve a ser marcado (latest insertedAt wins).
 *
 * Side effect: el consumer re-renderiza cada 1s mientras frozen.active === true.
 * Si solo necesitas active/exitFrozen sin countdown, lee del store directo.
 *
 * @returns {{ active: boolean, deadline: number|null, secondsLeft: number, countdownText: string, fullDayMode: boolean, exitFrozen: () => void }}
 */
export function useFrozenMode() {
  // Selectores finos: cada subscription se invalida solo cuando su dato cambia.
  // Evita re-renders por mutaciones en crossAgentDots/zombies que no nos interesan.
  const active = useCRMStore(s => s.frozen.active)
  const deadline = useCRMStore(s => s.frozen.deadline)
  const fullDayMode = useCRMStore(s => s.frozen.fullDayMode)
  const newRowsHighlight = useCRMStore(s => s.frozen.newRowsHighlight)
  const exitFrozen = useCRMStore(s => s.exitFrozen)
  const clearNewRowHighlight = useCRMStore(s => s.clearNewRowHighlight)

  const [now, setNow] = useState(Date.now())
  // phone -> { id, insertedAt } para detectar re-marks con timestamp distinto y reagendar.
  const highlightTimeoutsRef = useRef(new Map())

  // Tick cada 1s mientras frozen activo (para countdown UI).
  useEffect(() => {
    if (!active) return
    const iv = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(iv)
  }, [active])

  // setTimeout que descongela al expirar el deadline.
  // React garantiza que el cleanup corre antes del próximo body, asi que el id
  // capturado en el closure es el correcto.
  useEffect(() => {
    if (!active || !deadline) return
    const ms = Math.max(0, deadline - Date.now())
    const id = setTimeout(exitFrozen, ms)
    return () => clearTimeout(id)
  }, [active, deadline, exitFrozen])

  // Auto-clear de newRowsHighlight tras NEW_ROW_HIGHLIGHT_MS por phone.
  // Si el mismo phone se re-marca con un insertedAt distinto, cancelamos el
  // timeout viejo y agendamos uno nuevo (latest insertedAt wins).
  // clearNewRowHighlight es ref estable de Zustand, no es necesaria en deps.
  useEffect(() => {
    const timeouts = highlightTimeoutsRef.current

    // Agendar/reagendar timeouts para phones presentes en highlights.
    for (const phone of Object.keys(newRowsHighlight)) {
      const insertedAt = newRowsHighlight[phone]
      const existing = timeouts.get(phone)
      if (existing && existing.insertedAt === insertedAt) continue
      if (existing) clearTimeout(existing.id)
      const remainingMs = Math.max(0, NEW_ROW_HIGHLIGHT_MS - (Date.now() - insertedAt))
      const id = setTimeout(() => {
        timeouts.delete(phone)
        clearNewRowHighlight(phone)
      }, remainingMs)
      timeouts.set(phone, { id, insertedAt })
    }

    // Cancelar timeouts de phones que ya no están en highlights (ej: exitFrozen
    // limpia todo, o clearNewRowHighlight fue llamado externamente).
    for (const phone of Array.from(timeouts.keys())) {
      if (!(phone in newRowsHighlight)) {
        clearTimeout(timeouts.get(phone).id)
        timeouts.delete(phone)
      }
    }
  }, [newRowsHighlight]) // eslint-disable-line react-hooks/exhaustive-deps -- clearNewRowHighlight es ref estable de Zustand

  // Limpieza absoluta al desmontar: cancelar todos los timeouts pendientes.
  useEffect(() => {
    const timeouts = highlightTimeoutsRef.current
    return () => {
      for (const entry of timeouts.values()) clearTimeout(entry.id)
      timeouts.clear()
    }
  }, [])

  const secondsLeft = deadline
    ? Math.max(0, Math.ceil((deadline - now) / 1000))
    : 0

  // Formato: hh+min cuando >=1h (modo dia), m:ss cuando <1h (modo 3min).
  const countdownText = (() => {
    if (secondsLeft >= 3600) {
      const h = Math.floor(secondsLeft / 3600)
      const m = Math.floor((secondsLeft % 3600) / 60)
      return `${h}h ${m}m`
    }
    const m = Math.floor(secondsLeft / 60)
    const sec = secondsLeft % 60
    return `${m}:${sec.toString().padStart(2, '0')}`
  })()

  return {
    active,
    deadline,
    secondsLeft,
    countdownText,
    fullDayMode,
    exitFrozen,
  }
}
